import type { JSX } from 'react';
import { useState } from 'react';
import type { SurfaceOf } from './types';
import { useSurfaceActions } from './context';
import { useT } from '../i18n/useT';

type Form = SurfaceOf<'form'>;

export function FormSurfaceView({ surface }: { surface: Form }): JSX.Element {
  const t = useT();
  const actions = useSurfaceActions();
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(surface.fields.map((f) => [f.name, f.value ?? ''])));
  const [submitted, setSubmitted] = useState(false);
  const missing = new Set(surface.missing);
  const set = (name: string, v: string): void => setValues((prev) => ({ ...prev, [name]: v }));

  return (
    <form
      className="surface surface-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (submitted) return;
        setSubmitted(true);
        actions.submitForm(surface.formId, values);
      }}
    >
      <h3 className="surface-title">{surface.title}</h3>
      <div className="form-grid">
        {surface.fields.map((f) => {
          const id = `${surface.formId}-${f.name}`;
          const isMissing = missing.has(f.name);
          const common = { id, name: f.name, disabled: submitted, required: f.required, 'aria-invalid': isMissing || undefined };
          return (
            <div key={f.name} className={isMissing ? 'field field-missing' : 'field'}>
              <label htmlFor={id}>
                {f.label}
                {f.required ? ' *' : ''}
                {isMissing ? <span className="chip chip-warn small">{t('missingField')}</span> : null}
              </label>
              {f.kind === 'select' ? (
                <select {...common} value={values[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)}>
                  <option value="">—</option>
                  {(f.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : f.kind === 'textarea' ? (
                <textarea {...common} value={values[f.name] ?? ''} placeholder={f.placeholder} onChange={(e) => set(f.name, e.target.value)} rows={3} />
              ) : f.kind === 'checkbox' ? (
                <input {...common} type="checkbox" checked={values[f.name] === 'true'} onChange={(e) => set(f.name, e.target.checked ? 'true' : 'false')} />
              ) : (
                <input
                  {...common}
                  type={f.kind === 'phone' ? 'tel' : f.kind === 'email' ? 'email' : f.kind === 'number' ? 'number' : f.kind === 'date' ? 'date' : 'text'}
                  value={values[f.name] ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => set(f.name, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={submitted}>
          {surface.submitLabel}
        </button>
        {submitted ? <span className="muted small">{t('submitted')}</span> : null}
      </div>
    </form>
  );
}
